/**
 * Screening contract (F15, milestone M3): dual-pass decisions, the one
 * current outcome per record and stage, the human queue, summaries,
 * exclusions and PRISMA screening counts. Re-exported from index.ts.
 *
 * Owner: screening builder (F15). Shapes follow
 * specs/features/F15-screening/design.md, Interfaces.
 */
import { pagePath } from './paths';
import type { RecordAuthor, RecordListing } from './search';

export const SCREENING_ROUTES = {
  /** GET ?stage&reason&limit&cursor -> 200 ScreeningQueueResponse (E30; SCRN-058). */
  screeningQueue: (slug: string) => `${pagePath(slug)}/screening/queue`,
  /**
   * POST ScreeningDecisionRequest -> 201 ScreeningDecisionResponse (E31; SCRN-050 to SCRN-056).
   * GET -> 200 RecordScreeningResponse (E103; SCRN-091).
   */
  recordScreening: (slug: string, recordId: string) => `${pagePath(slug)}/records/${encodeURIComponent(recordId)}/screening`,
  /** POST NextItemRequest -> 200 NextItemResponse (E99; SCRN-054). */
  screeningQueueNext: (slug: string) => `${pagePath(slug)}/screening/queue/next`,
  /** POST {stage} -> 200 LeaseRenewResponse (E100; SCRN-067). */
  screeningQueueRenew: (slug: string, recordId: string) => `${pagePath(slug)}/screening/queue/${encodeURIComponent(recordId)}/renew`,
  /** POST {stage} -> 204 (E101; SCRN-054). */
  screeningQueueRelease: (slug: string, recordId: string) => `${pagePath(slug)}/screening/queue/${encodeURIComponent(recordId)}/release`,
  /** POST ScreeningUndoRequest -> 200 ScreeningDecisionResponse (E102; SCRN-059). */
  recordScreeningUndo: (slug: string, recordId: string) => `${pagePath(slug)}/records/${encodeURIComponent(recordId)}/screening/undo`,
  /** GET -> 200 ScreeningSummary (E104; SCRN-069, SCRN-092). */
  screeningSummary: (slug: string) => `${pagePath(slug)}/screening/summary`,
  /** GET ?stage&limit&cursor -> 200 ExclusionListResponse (E105; SCRN-079). */
  screeningExclusions: (slug: string) => `${pagePath(slug)}/screening/exclusions`,
  /** PUT ScreeningSettingsRequest -> 200 ScreeningSettings (E106; SCRN-088). */
  screeningSettings: (slug: string) => `${pagePath(slug)}/screening/settings`,
  /** GET -> 200 ScreeningCostEstimate (E107; SCRN-096). */
  screeningEstimate: (slug: string) => `${pagePath(slug)}/screening/estimate`,
} as const;

export type ScreeningStage = 'title_abstract' | 'full_text';
export type OutcomeState = 'pending' | 'awaiting_full_text' | 'agreed' | 'awaiting_human' | 'human_resolved' | 'superseded';
export type QueueReason =
  | 'disagreement'
  | 'uncertain'
  | 'model_failed'
  | 'fallback_basis'
  | 'confirm_exclusion'
  | 'audit_sample'
  | 'duplicate_conflict';
export type ScreeningBasis = 'title_abstract' | 'title_only' | 'full_text' | 'abstract_and_registry';
export type ExclusionMode = 'automatic' | 'confirm';
export type ModeReason = 'owner' | 'no_passing_gate' | 'audit_misses';

/**
 * RunWarning codes the screen stage adds (F15 design, Screen stage): the spend
 * cap was reached (SCRN-026), or no model is configured for screening, so the
 * records wait, still pending, for the next run.
 */
/** 'plan_limit_reached': the workspace plan's AI-screened record allowance is used up (F24 BILL-085). */
export type ScreeningRunWarningCode = 'budget_exhausted' | 'ai_unavailable' | 'plan_limit_reached';

/** details.reason of a 409 conflict from the screening endpoints (F15 design, Error codes). */
export type ScreeningConflictReason =
  | 'version_mismatch'
  | 'leased'
  | 'undo_window_passed'
  | 'no_passing_gate'
  | 'duplicate_record'
  | 'stage_not_open';

export interface ScreeningQuote {
  field: 'title' | 'abstract' | 'title_abstract' | 'full_text';
  /** As it appears in the stored text. */
  text: string;
  /** UTF-16 offsets into the stored field (title_abstract: title + ' ' + abstract). */
  start: number | null;
  end: number | null;
  /** full_text only (SCRN-023). */
  spanId: string | null;
  /** full_text only. */
  page: number | null;
}

export interface ScreeningDecision {
  /** 'sd_<uuid>'. */
  id: string;
  recordId: string;
  stage: ScreeningStage;
  round: number;
  kind: 'pass' | 'human' | 'undo';
  /** 1 = screening_pass_a, 2 = screening_pass_b; null for people. */
  pass: 1 | 2 | null;
  verdict: 'include' | 'exclude' | 'uncertain';
  /** Primary rule (SCRN-024): an eligibility rule ID of the frozen protocol. */
  ruleId: string | null;
  ruleIds: string[];
  quote: ScreeningQuote | null;
  rationale: string;
  basis: ScreeningBasis;
  truncated: boolean;
  /** 'model:anthropic/<serving model>' or 'user:<id>'. */
  actor: string;
  model: { provider: 'anthropic'; servedModel: string; requestedModel: string; promptVersion: string; modelCallId: string } | null;
  /** Human decisions: the queue reason they answered. */
  answeredReason: QueueReason | null;
  undoesDecisionId: string | null;
  runId: string | null;
  createdAt: string;
}

export interface ScreeningOutcome {
  recordId: string;
  stage: ScreeningStage;
  state: OutcomeState;
  verdict: 'include' | 'exclude' | null;
  /** Exclusion reason for excluded records: an eligibility rule ID. */
  ruleId: string | null;
  /** confirm_exclusion. */
  suggestedRuleId: string | null;
  queueReason: QueueReason | null;
  /** For example the failure kind for model_failed. */
  queueDetail: string | null;
  queuedAt: string | null;
  basis: ScreeningBasis | null;
  truncated: boolean;
  audit: { selected: boolean; status: 'none' | 'pending' | 'confirmed' | 'overturned' };
  round: number;
  version: number;
  combinationRuleVersion: string | null;
  lease: { userId: string; displayName: string; expiresAt: string } | null;
  decidedBy: string | null;
  decidedAt: string | null;
}

/** Label per SCRN-066. */
export interface EligibilityRuleView {
  id: string;
  kind: 'include' | 'exclude';
  text: string;
  label: string;
}

export interface QueueItem {
  record: RecordListing & { abstract: string | null; authorsAll: RecordAuthor[] };
  outcome: ScreeningOutcome;
  rules: EligibilityRuleView[];
  /** SCRN-037, SCRN-063. */
  blind: boolean;
  /** Null when blind. */
  passes: { a: ScreeningDecision | null; b: ScreeningDecision | null } | null;
  fullText: { documentId: string; sourceViewUrl: string; storagePolicy: 'full' | 'spans_only' } | null;
}

/** GET SCREENING_ROUTES.screeningQueue (SCRN-058; api.md E30). */
export interface ScreeningQueueResponse {
  items: QueueItem[];
  nextCursor: string | null;
  counts: Partial<Record<QueueReason, number>>;
  /** The caller's own decisions since 00:00 UTC, for the progress line (SCRN-060). */
  decidedToday: number;
}

/** POST screening/queue/next (E99; SCRN-054). */
export interface NextItemRequest {
  stage: ScreeningStage;
  reason?: QueueReason;
  /** Record ids the reviewer skipped in this session, passed over even when unleased (at most 200). */
  skip?: string[];
}

export interface NextItemResponse {
  item: QueueItem | null;
}

/** POST screening/queue/{recordId}/renew and release (E100, E101; SCRN-054, SCRN-067). */
export interface LeaseRequest {
  stage: ScreeningStage;
}

export interface LeaseRenewResponse {
  leaseExpiresAt: string;
}

/** POST SCREENING_ROUTES.recordScreening (SCRN-050 to SCRN-053; api.md E31). */
export interface ScreeningDecisionRequest {
  stage: ScreeningStage;
  verdict: 'include' | 'exclude';
  ruleId: string | null;
  quote: { field: 'title' | 'abstract' | 'full_text'; text: string } | null;
  /** 0 to 1 000 characters; 10 to 1 000 when overriding (SCRN-052). */
  rationale: string;
  expectedVersion: number;
}

export interface ScreeningDecisionResponse {
  decision: ScreeningDecision;
  outcome: ScreeningOutcome;
  /** Revealed after a blind decision. */
  passes: { a: ScreeningDecision | null; b: ScreeningDecision | null };
  /** SCRN-013, SCRN-056. */
  next: { fullText: ScreeningOutcome | null };
}

/** POST records/{recordId}/screening/undo (E102; SCRN-059). */
export interface ScreeningUndoRequest {
  stage: ScreeningStage;
  decisionId: string;
}

/** GET records/{recordId}/screening (E103; SCRN-091). */
export interface RecordScreeningResponse {
  outcomes: ScreeningOutcome[];
  decisions: ScreeningDecision[];
}

export interface StageSummary {
  byState: Record<OutcomeState, number>;
  agreed: { include: number; exclude: number };
  awaitingByReason: Partial<Record<QueueReason, number>>;
  humanResolved: { include: number; exclude: number };
  audit: { selected: number; decided: number; overturned: number; rate: number };
  recallEstimate:
    | { value: number; low: number; high: number; m: number; mLow: number; mHigh: number }
    | { value: null; reason: 'no_audit_data' };
}

/** GET screening/summary (E104; SCRN-069, SCRN-092). */
export interface ScreeningSummary {
  mode: { value: ExclusionMode; reason: ModeReason | null; note: string | null; gate: { passing: boolean; reportId: string | null } };
  stages: Record<ScreeningStage, StageSummary>;
}

/** PUT screening/settings (E106; SCRN-088). */
export interface ScreeningSettingsRequest {
  exclusionMode: ExclusionMode;
  /** 1 to 500 characters. */
  note: string;
  /** 0 to 1, at least 0.02 while automatic. */
  auditRates?: { title_abstract: number; full_text: number };
}

export type ScreeningSettings = ScreeningSummary['mode'] & { auditRates: { title_abstract: number; full_text: number } };

export interface ExclusionRow {
  recordId: string;
  title: string;
  year: number | null;
  doi: string | null;
  pmid: string | null;
  registryId: string | null;
  source: 'automation' | 'reviewer';
  /** The exclusion reason: an eligibility rule ID of the frozen protocol. */
  ruleId: string;
  label: string;
  quote: ScreeningQuote | null;
  decidedAt: string;
}

/** GET screening/exclusions (E105; SCRN-079). */
export interface ExclusionListResponse {
  rows: ExclusionRow[];
  nextCursor: string | null;
}

/** GET screening/estimate (E107; SCRN-096). */
export interface ScreeningCostEstimate {
  awaiting: Record<ScreeningStage, number>;
  /** Rounded to cents. */
  usd: number;
  basis: 'page_history' | 'defaults';
}

/** F15's part of the PRISMA flow, per column (SCRN-070 to SCRN-078). */
export interface PrismaScreeningColumn {
  titleAbstract: {
    screened: number;
    excluded: { total: number; byAutomation: number; byReviewer: number };
    included: number;
    awaiting: number;
  };
  retrieval: { reportsSought: number; reportsNotRetrieved: number; retrievalPending: number };
  fullText: {
    assessed: number;
    excluded: { total: number; reasons: { ruleId: string; label: string; count: number }[] };
    awaiting: number;
    assessedOnFallback: { decided: number; included: number; excluded: number };
  };
  included: { studies: number; reports: number };
}

export interface PrismaScreening {
  scope: { runId: string } | 'cumulative';
  databasesAndRegisters: PrismaScreeningColumn;
  otherMethods: PrismaScreeningColumn;
}
